import React, { useState } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import * as PropTypes from "prop-types";

import {
  CLIENT_COSTS,
  TEACHER_COSTS,
  GROUP_CLASS_TYPES,
} from "../common/Const";
import { getClassTypeName } from "../common/Helpers";

const AttendanceReportPaymentModal = ({ selected, forClient = false }) => {
  const [toggle, setToggle] = useState(false);

  const handleToggle = () => setToggle(prevToggle => !prevToggle);

  const calculateTeacherClassCost = (reg, classType) => {
    const maxMembers = reg.maxMembers || 0;
    const presentCount = reg.presentCount || 0;
    const costPerClass = TEACHER_COSTS[classType] || 0;

    if (GROUP_CLASS_TYPES.includes(classType)) {
      return parseFloat(
        ((costPerClass / maxMembers) * presentCount).toFixed(2)
      );
    }
    return costPerClass;
  };

  const organizeDataByClassType = (data) => {
    const classTypeData = {};

    data.forEach((reg) => {
      const {classType} = reg;
      const presentCount = reg.presentCount || 0;
      const key = `${classType}-${presentCount}`;

      if (!classTypeData[key]) {
        classTypeData[key] = {
          classType,
          count: 0,
          presentCount,
          totalCost: 0,
        };
      }

      const costPerClass = forClient
        ? CLIENT_COSTS[classType] || 0
        : calculateTeacherClassCost(reg, classType);

      classTypeData[key].count += 1;
      classTypeData[key].totalCost += costPerClass;
    });

    return classTypeData;
  };

  const organizeDataByClientAndStudent = (data) => {
    const clientData = {};

    data.forEach((reg) => {
      reg.students.forEach((student) => {
        const {clientName, studentName} = student;
        const {classType} = reg;

        if (!clientData[clientName]) {
          clientData[clientName] = {};
        }
        if (!clientData[clientName][studentName]) {
          clientData[clientName][studentName] = {};
        }
        if (!clientData[clientName][studentName][classType]) {
          clientData[clientName][studentName][classType] = {
            count: 0,
            totalCost: 0,
          };
        }

        const costPerClass = CLIENT_COSTS[classType] || 0;
        clientData[clientName][studentName][classType].count += 1;
        clientData[clientName][studentName][classType].totalCost +=
          costPerClass;
      });
    });

    return clientData;
  };

  const calculateTotalCost = data => Object.values(data).reduce((sum, item) => {
    if (forClient) {
      return (
        sum +
          Object.values(item).reduce(
            (studentSum, classTypes) => Object.values(classTypes).reduce(
              (classSum, { totalCost: classTypeTotalCost }) => classSum + classTypeTotalCost,
              0
            ),
            0
          )
      );
    }
    return sum + item.totalCost;
  }, 0);

  const calculateClientTotal = studentData => Object.values(studentData).reduce((clientSum, classes) => (
    clientSum +
        Object.values(classes).reduce(
          (classSum, { totalCost: classTypeTotalCost }) => classSum + classTypeTotalCost,
          0
        )
  ), 0);

  const organizedData = forClient
    ? organizeDataByClientAndStudent(selected)
    : organizeDataByClassType(selected);
  const totalCost = parseFloat(calculateTotalCost(organizedData).toFixed(2));

  const sortedData = !forClient
    ? Object.entries(organizedData).sort(([, dataA], [, dataB]) => {
      const nameA = getClassTypeName(dataA.classType).toLowerCase();
      const nameB = getClassTypeName(dataB.classType).toLowerCase();
      return nameA.localeCompare(nameB);
    })
    : null;

  let modalBody;

  if (selected.length > 0) {
    if (forClient) {
      modalBody = Object.entries(organizedData).map(([clientName, studentData]) => (
        <div key={clientName} className="client-section">
          <h5 className="client-name report-modal-title mb-3">
              Klient: {clientName}
          </h5>
          <Table className="mb-4" bordered>
            <thead>
              <tr>
                <th>Imię ucznia</th>
                <th>Typ lekcji</th>
                <th>Ilość</th>
                <th>Suma</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(studentData).map(
                ([studentName, classes], index) => (
                  <React.Fragment key={studentName}>
                    <tr
                      className={`student-row ${
                        index % 2 === 0 ? "" : "report-modal-table-row"
                      }`}
                    >
                      <td colSpan="4" className="report-modal-title">
                        {studentName}
                      </td>
                    </tr>
                    {Object.entries(classes).map(
                      ([classType, { count, totalCost: classTypeTotalCost }]) => (
                        <tr
                          key={`${studentName}-${classType}`}
                          className={`class-type-row ${
                            index % 2 === 0 ? "" : "report-modal-table-row"
                          }`}
                        >
                          <td />
                          <td>{getClassTypeName(classType)}</td>
                          <td>{count}</td>
                          <td>{classTypeTotalCost.toFixed(2)} zł</td>
                        </tr>
                      )
                    )}
                  </React.Fragment>
                )
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3" className="font-weight-bold text-right">
                    Suma płatności dla klienta:
                </td>
                <td className="font-weight-bold">
                  {calculateClientTotal(studentData).toFixed(2)} zł
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
      ));
    } else {
      modalBody = (
        <Table className="mb-4" bordered>
          <thead>
            <tr>
              <th>Typ lekcji</th>
              <th>Ilość</th>
              <th>Obecni</th>
              <th>Suma</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map(
              ([key, { classType, count, presentCount, totalCost: classTypeTotalCost }]) => (
                <tr key={key}>
                  <td>{getClassTypeName(classType)}</td>
                  <td>{count}</td>
                  <td>{presentCount}</td>
                  <td>{classTypeTotalCost.toFixed(2)} zł</td>
                </tr>
              )
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="3" className="font-weight-bold text-right">
                  Suma płatności dla nauczyciela:
              </td>
              <td className="font-weight-bold">{totalCost} zł</td>
            </tr>
          </tfoot>
        </Table>
      );
    }
  } else {
    modalBody = "Brak wybranych lekcji";
  }
    
  return (
    <>
      <Button
        className={`btn ${
          forClient ? "btn-secondary" : "btn-primary"
        } manage-button btn-block h-100 p-1`}
        variant="primary"
        onClick={handleToggle}
      >
        Wylicz płatność ({forClient ? "klient" : "nauczyciel"})
      </Button>

      <Modal size="lg" show={toggle} onHide={handleToggle} centered>
        <Modal.Header closeButton>
          <Modal.Title className="font-weight-bold">{`Płatność dla ${
            forClient ? "klienta" : "nauczyciela"
          }`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalBody}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="manage-button manage-red-button btn btn-primary"
            onClick={handleToggle}
          >
            Zamknij
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

AttendanceReportPaymentModal.propTypes = {
  selected: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    dayOfWeek: PropTypes.string.isRequired,
    startTime: PropTypes.string.isRequired,
    classType: PropTypes.string.isRequired,
    teacherName: PropTypes.string.isRequired,
    presentCount: PropTypes.number.isRequired,
    maxMembers: PropTypes.number.isRequired,
    students: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      isPresent: PropTypes.bool,
      clientId: PropTypes.string.isRequired,
      clientName: PropTypes.string.isRequired,
      included: PropTypes.bool.isRequired,
      studentName: PropTypes.string.isRequired,
      comment: PropTypes.string.isRequired,
    })),
  })).isRequired,
  forClient: PropTypes.bool,
};


AttendanceReportPaymentModal.defaultProps = {
  forClient: false,
};

export default AttendanceReportPaymentModal;
